"use client";

import React from "react";
import { MessageCircle, Loader2 } from "lucide-react";
import { Position } from "./use-draggable-bubble";

export interface AgentChatBubbleProps {
    bubblePos: Position | null;
    isDragging: boolean;
    isSnapping: boolean;
    isLoading: boolean;
    isListening?: boolean;
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    className?: string;
}

/**
 * Draggable Floating Chat Bubble component that renders in collapsed state.
 * Styled after modern iOS Messages identity with emerald-green gradient,
 * solid white speech bubble icon, and active voice indicator.
 */
export const AgentChatBubble: React.FC<AgentChatBubbleProps> = ({
    bubblePos,
    isDragging,
    isSnapping,
    isLoading,
    isListening = false,
    onPointerDown,
    className = "",
}) => {
    const stopPropagation = (e: React.SyntheticEvent) => {
        e.stopPropagation();
    };

    return (
        <div
            data-agent-chat-bubble="true"
            onMouseDown={stopPropagation}
            onTouchStart={stopPropagation}
            onClick={stopPropagation}
            onPointerDown={onPointerDown}
            style={
                bubblePos
                    ? {
                          position: "fixed",
                          top: 0,
                          left: 0,
                          transform: `translate3d(${bubblePos.x}px, ${bubblePos.y}px, 0)`,
                          transition: isSnapping
                              ? "transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1)"
                              : "none",
                          touchAction: "none",
                          userSelect: "none",
                          zIndex: 50,
                      }
                    : undefined
            }
            className={`fixed z-50 pointer-events-auto touch-none select-none ${
                !bubblePos ? "bottom-[108px] right-4" : ""
            } ${className}`}
        >
            <div
                title="Chạm để mở khung nhắn tin AI • Kéo để di chuyển"
                className={`relative flex items-center justify-center w-[50px] h-[50px] sm:w-[54px] sm:h-[54px] rounded-full bg-[#0c121e]/90 hover:bg-[#141d2e] border-2 border-white/25 shadow-[0_12px_36px_rgba(0,0,0,0.65)] hover:border-emerald-400 hover:shadow-[0_0_24px_rgba(34,197,94,0.6)] transition-all cursor-grab active:cursor-grabbing group active:scale-95 ${
                    isDragging ? "scale-105 border-emerald-400 ring-4 ring-emerald-500/30" : ""
                }`}
            >
                {/* iOS Messages-style Chat Bubble Icon */}
                <div className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-tr from-[#16a34a] via-[#22c55e] to-[#4ade80] text-white shadow-md group-hover:scale-105 transition-transform">
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                    ) : (
                        <MessageCircle className="h-4.5 w-4.5 sm:h-5 sm:w-5 text-white fill-white drop-shadow-sm" />
                    )}
                </div>

                {/* Active Voice Listening indicator badge */}
                {isListening && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-red-500 border border-white" />
                    </span>
                )}

                {/* Subtle Pulse ring to hint interactivity */}
                <div className="absolute inset-0 rounded-full border border-emerald-400/20 animate-pulse pointer-events-none" />
            </div>
        </div>
    );
};

export default AgentChatBubble;
